import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/roof-repair";
const NAME = "Roof Repair";
const TITLE = "Roof Repair in Phoenix — All Types | Durafoam Roofing";
const DESCRIPTION =
  "Phoenix roof repair for foam, tile, shingle, flat, and commercial roofs. Family-owned since 1989 — written estimates, honest scope, real warranties.";

const faqs = [
  {
    q: "What roof types do you repair?",
    a: "All of them: spray foam (SPF), concrete and clay tile, asphalt shingle, flat single-ply (TPO, PVC, modified bitumen), and built-up roofs. If it's on a Phoenix-area home or building, we've repaired it.",
  },
  {
    q: "Do I need a full replacement or just repairs?",
    a: "Most of the time, repair. We won't push you toward a re-roof unless the existing system has failed structurally or is so far past life that repairs become throwing good money after bad. We'll show you photos and walk through the decision.",
  },
  {
    q: "How fast can you come out?",
    a: "For active leaks, same-week and often within 24–48 hours during monsoon. Routine repair inspections are typically scheduled within a few business days.",
  },
  {
    q: "Do you offer warranties on repairs?",
    a: "Yes. Every repair is backed by a written workmanship warranty. Length depends on the scope.",
  },
  {
    q: "Will you give a written estimate before starting work?",
    a: "Always. We don't do verbal estimates or surprise charges. You see the line-item scope and price before we lift a tool.",
  },
  {
    q: "How much does roof repair cost in Phoenix?",
    a: "It depends on the roof type and the scope of the failure — a targeted flashing or tile repair is a few hundred dollars, while larger multi-point repairs run higher. Most Phoenix roof repairs land in the hundreds to low thousands. See our roof repair cost guide for real ranges, or book a free inspection for an exact written number.",
    aNode: <>It depends on the roof type and the scope of the failure — a targeted flashing or tile repair is a few hundred dollars, while larger multi-point repairs run higher. Most Phoenix roof repairs land in the hundreds to low thousands. See our{" "}<Link to="/roof-repair-cost-phoenix" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">roof repair cost guide</Link>{" "}for real ranges, or book a free inspection for an exact written number.</>,
  },
  {
    q: "How do I know if I have roof damage after a storm?",
    a: "From the ground, look for slipped or broken tiles, missing shingles, debris impacts, and bent flashing. Inside, check for fresh ceiling stains or musty smells in the days after a monsoon. But most storm damage isn't visible from the ground — that's why we offer a free inspection with photos, so you know exactly what the storm did before filing a claim or paying for anything.",
  },
];

export const Route = createFileRoute("/roof-repair")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="roof-repair"
      extraSections={SEARCH_TERM_SECTIONS["roof-repair"]}
      eyebrow="Roof Repair"
      h1="Roof Repair in Phoenix — All Roof Types"
      subhead="Honest diagnosis, surgical scope, and repairs that hold. We work on foam, tile, shingle, flat, and commercial roofs across the entire Valley."
      heroBullets={[
        "Foam, tile, shingle, flat, and commercial",
        "Leak source diagnosed, not just patched",
        "Written estimates, no surprise charges",
        "Workmanship warranty on every repair",
      ]}
      whatIs={{
        heading: "What a real roof repair looks like",
        body: (
          <>
            <p>
              A roof repair done right starts with diagnosis. Water rarely drips directly below the
              hole — it travels along the underside of the deck and surfaces wherever gravity sends
              it. Repairing the spot above the stain without finding the actual failure on the roof
              is how customers end up calling three different roofers about the same leak.
            </p>
            <p>
              Durafoam's repair crews carry tooling for every common Phoenix roof type. On tile we
              re-bed cracked or slipped pieces and rebuild underlayment-level flashings. On shingle
              we replace damaged shingles, repair pipe boots, and address valley failures. On
              foam we cut out failed foam, refoam, and recoat. On flat single-ply we weld in
              patches and reseat penetrations.
            </p>
            <p>
              The scope is always proportional to the problem. If you have one bad pipe boot, you
              need one pipe boot replaced — not a full re-roof. We tell customers that constantly
              because it's true.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Phoenix conditions that drive most repair calls",
        body: (
          <>
            <p>
              Roofs in the Valley don't fail randomly — they fail in patterns tied to our specific
              climate. Knowing the pattern speeds up the diagnosis.
            </p>
            <ul>
              <li>
                <strong>Monsoon wind-driven rain:</strong> Lifts shingles, drives water under tile,
                and exposes any seam or flashing that isn't sealed tight. Most leak calls cluster
                in the 48 hours after a big monsoon cell. If hail or a wind event caused it, our{" "}
                <Link to="/storm-damage-repair">storm &amp; hail damage repair</Link> page covers
                how we document it for your insurance claim.
              </li>
              <li>
                <strong>Heat-driven flashing failure:</strong> Sealants and pipe boots dry, crack,
                and shrink under sustained 110°F+ days. Pipe boots in particular are a known
                consumable in Phoenix — most need replacement at 7–10 years.
              </li>
              <li>
                <strong>Thermal cycling:</strong> A 60–80°F daily swing flexes every fastener and
                seam. Slipped tiles, popped nails, and split sealant joints all trace back to
                thermal movement.
              </li>
              <li>
                <strong>Foot traffic:</strong> HVAC service, satellite installs, and DIY trips
                onto fragile tile are surprisingly common causes of leaks we end up repairing.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our roof repair process",
        steps: [
          {
            title: "Free on-site inspection",
            body: "An estimator walks the roof, traces the leak to its source, and photographs everything. You get the photos with your estimate.",
          },
          {
            title: "Written, line-item estimate",
            body: "Scope, materials, price, and warranty all in writing before any work begins. No verbal handshakes, no surprises on the invoice.",
          },
          {
            title: "Repair done right",
            body: "Crew matches roof type. We use manufacturer-approved materials and standard practices for tile, shingle, foam, and flat systems.",
          },
          {
            title: "Walkthrough and warranty",
            body: "We show you the finished repair and back it in writing. Free recall if the same area leaks within the warranty term.",
          },
        ],
      }}
      faqs={faqs}
      ctaTitle="Leak or damage you want eyes on?"
      ctaBody="We'll come out, diagnose the real problem, and give you a written estimate — no obligation, no upsell."
    />
  );
}
